import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Utilities } from '../../utilities';
import { eAssetType, eDatabaseType, eLogSeverity, eSystemEventType } from '../../enums';

/** Numeric enums: the API may send the number, translations are keyed by member name. */
const NUMERIC_ENUMS: Record<string, Record<string | number, string | number>> = {
  assetType: eAssetType,
  databaseType: eDatabaseType,
  logSeverity: eLogSeverity,
  systemEventType: eSystemEventType,
};

/**
 * Translates enum values (and enumerated types returned by the API) using the
 * `enums.<enumName>.<value>` keys in the reg-hub-common translation files.
 *
 * Lookup order:
 *  1. `enums.<enumName>.<value>`
 *  2. `enums.<enumName>.<fallbackName without spaces/punctuation>` (API types whose id is numeric)
 *  3. the fallback name as given, or the value with spaces added ("RequestedAtRegistry" -> "Requested At Registry")
 */
@Injectable({ providedIn: 'root' })
export class EnumLabelService {
  private readonly transloco = inject(TranslocoService);

  constructor() {
    // Lets static helpers (Utilities.getPartyTypeString etc.) translate without DI
    Utilities.enumLabel = (enumName, value, fallbackName) => this.label(enumName, value, fallbackName);
  }

  label(enumName: string, value: string | number | boolean | null | undefined, fallbackName?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return fallbackName ?? '';
    }

    const numeric = NUMERIC_ENUMS[enumName];
    if (numeric && (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value)))) {
      const memberName = numeric[Number(value)];
      if (typeof memberName === 'string') {
        value = memberName;
      }
    }

    const byValue = this.lookup(enumName, String(value));
    if (byValue !== null) {
      return byValue;
    }

    if (fallbackName) {
      const byName = this.lookup(enumName, fallbackName.replace(/[^A-Za-z0-9_]/g, ''));
      return byName ?? fallbackName;
    }

    return typeof value === 'string' ? Utilities.spacePascalCase(value) : String(value);
  }

  /** True when a translation exists for this value. */
  has(enumName: string, value: string | number | boolean | null | undefined): boolean {
    return value !== null && value !== undefined && this.lookup(enumName, String(value)) !== null;
  }

  private lookup(enumName: string, value: string): string | null {
    if (!value) {
      return null;
    }
    const key = `enums.${enumName}.${value}`;
    const text = this.transloco.translate(key);
    return text && text !== key ? text : null;
  }
}
